import {
  jobStateToJobInput,
  scheduleDates,
  Billing,
} from '../../../../context';
import { JobDraftState, Schedule } from '../../../context';

import { JobTypeEnum, VisibilityStatusEnum } from '@/types/graphql';

type ShiftPayload = {
  start_at: string;
  end_at: string;
  time_zone_offset?: number;
};

type WorkerDetail = {
  worker_id: number;
  worker_name: string;
  job_skill_id: number;
  job_skill_name: string;
  schedule_group_id: number;
  start_date: string;
  end_date: string;
  start_time: string;
  end_time: string;
  shift_dates: string[];
};

type JobPayload = {
  address_id: string;
  contact_id: string;
  skill_id: string;
  uniform_id: string;
  quantity: number;
  shifts: ShiftPayload[];
  rate_id?: string | null;
  pay_rate?: number;
  cost_rate?: number;
  original_pay_rate?: number;
  original_cost_rate?: number;
  job_type?: 'GIG' | 'LTA';
  group_id?: number;
  all_shifts_same_worker?: boolean;
  is_holiday?: boolean;
  mandatory_break_time?: number;
  auto_accept_requests?: boolean;
  instructions?: string | null;
  address_instructions?: string;
  contact_instructions?: string;
  uniform_instructions?: string;
  share_contact_number?: boolean;
  publish?: boolean;
  publish_in?: number | null;
  visibility?: VisibilityStatusEnum;
  certificates?: string[];
  invite_workers?: string[];
  hire_workers?: string[];
  invited_worker_details?: WorkerDetail[];
  hired_worker_details?: WorkerDetail[];
};

export type CreateOrderPayload = {
  account_id: string;
  verification_enabled?: boolean;
  jobs: JobPayload[];
};

const buildWorkerDetails = (
  workers: Schedule['selectedWorkers'],
  job: JobDraftState,
  schedule: Schedule | undefined,
): WorkerDetail[] => {
  if (!workers || workers.length === 0 || !schedule) return [];

  const shiftDates = scheduleDates(schedule).map(
    (date) => date.toISOString().split('T')[0],
  );
  const startDate =
    schedule.dateRange?.startDate?.toISOString().split('T')[0] || '';
  const endDate =
    schedule.dateRange?.endDate?.toISOString().split('T')[0] || '';

  return workers.map((worker: any) => ({
    worker_id: Number(worker.id),
    worker_name: worker.user
      ? `${worker.user.firstName} ${worker.user.lastName}`
      : worker.name || '',
    job_skill_id: Number(job.skill?.id),
    job_skill_name: job.skill?.name || '',
    schedule_group_id: schedule.groupId,
    start_date: startDate,
    end_date: endDate,
    start_time: schedule.startTime,
    end_time: schedule.endTime,
    shift_dates: shiftDates,
  }));
};

export const buildOrderPayload = (
  billing: Billing,
  orderType: JobTypeEnum,
  jobs: JobDraftState[],
): CreateOrderPayload => {
  const allJobs = [...jobs];
  let index = 1;

  const jobPayloads: JobPayload[] = [];

  // Check if any job has verification_enabled
  const hasVerificationEnabled = jobs.some(
    (job) => job.verification_enabled === true,
  );

  allJobs.forEach((job) => {
    const jobInputs = jobStateToJobInput(billing, orderType, job, index);

    if (jobInputs && jobInputs.length) {
      index = jobInputs[jobInputs.length - 1].groupId! + 1;

      // Map each jobInput (which corresponds to a schedule) to a REST payload
      jobInputs.forEach((jobInput, scheduleIdx) => {
        const schedule = job.schedules[scheduleIdx];

        const jobPayload: JobPayload = {
          // Required fields
          address_id: jobInput.addressId,
          contact_id: jobInput.contactId,
          skill_id: jobInput.skillId,
          uniform_id: jobInput.uniformId,
          quantity: jobInput.quantity,
          shifts: jobInput.shifts.map((shift) => ({
            start_at: shift.startAt,
            end_at: shift.endAt,
            time_zone_offset: shift.timeZoneOffset ?? undefined,
          })),

          // Rate fields
          rate_id: jobInput.rateId ?? undefined,
          pay_rate: jobInput.payRate ?? undefined,
          cost_rate: jobInput.costRate ?? undefined,
          original_pay_rate: jobInput.originalPayRate ?? undefined,
          original_cost_rate: jobInput.originalCostRate ?? undefined,

          // Configuration fields
          job_type: jobInput.jobType ?? undefined,
          group_id: jobInput.groupId ?? undefined,
          all_shifts_same_worker: jobInput.allShiftsSameWorker ?? undefined,
          is_holiday: jobInput.isHoliday ?? undefined,
          mandatory_break_time: jobInput.mandatoryBreakTime ?? undefined,

          // Instructions
          instructions: jobInput.instructions ?? undefined,
          address_instructions: jobInput.addressInstructions ?? undefined,
          contact_instructions: jobInput.contactInstructions ?? undefined,
          uniform_instructions: jobInput.uniformInstructions ?? undefined,
          share_contact_number: jobInput.shareContactNumber ?? undefined,

          // Publishing
          publish: jobInput.publish ?? undefined,
          publish_in: jobInput.publishIn ?? undefined,
          visibility: jobInput.visibility ?? undefined,

          // Certificates
          certificates: jobInput.certificates ?? undefined,

          // Workers
          invite_workers: jobInput.inviteWorkers ?? undefined,
          hire_workers: jobInput.hireWorkers ?? undefined,

          // Extended worker details
          invited_worker_details: buildWorkerDetails(
            schedule?.selectedWorkers || [],
            job,
            schedule,
          ),
          hired_worker_details: buildWorkerDetails(
            schedule?.hiredWorkers || [],
            job,
            schedule,
          ),
        };

        jobPayloads.push(jobPayload);
      });
    }
  });

  return {
    account_id: billing.account.id,
    verification_enabled: hasVerificationEnabled,
    jobs: jobPayloads,
  };
};
